import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import UseApi from 'hooks/useApi';
import Button from 'components/button';
import Card from 'components/card';
import Loader from 'components/loader';
import Header from 'components/text/header';
import { useAlert } from 'context/alertContext';
import { HiArrowLeft } from 'react-icons/hi';

const queryClient = new QueryClient();

const CustomTripDetailsContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setSuccessMessage, setErrorMessage } = useAlert();
  const { getApi, patchApi, deleteApi } = UseApi();
  
  const [status, setStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  async function fetchData(id: string) {
    const response = await getApi(`customTrips/${id}`);
    return response.data;
  }

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['customTrip', id],
    queryFn: () => fetchData(id!),
  });

  useEffect(() => {
    if (data) {
      setStatus(data.status);
      setAdminNotes(data.adminNotes || '');
    }
  }, [data]);

  const handleStatusUpdate = async () => {
    setUpdateLoading(true);
    try {
      const response = await patchApi(`customTrips/${id}/status`, { status, adminNotes });
      if (response) {
        setSuccessMessage('Custom trip status updated successfully');
        setIsEditing(false);
        refetch();
      }
    } catch (error) {
      setErrorMessage('Failed to update custom trip status');
    }
    setUpdateLoading(false);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this custom trip request?')) {
      setDeleteLoading(true);
      try {
        const response = await deleteApi(`customTrips/${id}`);
        if (response) {
          setSuccessMessage('Custom trip deleted successfully');
          navigate('/admin/custom-trips');
        }
      } catch (error) {
        setErrorMessage('Failed to delete custom trip');
      }
      setDeleteLoading(false);
    }
  };

  if (isLoading) return <Loader />;
  if (error) return <Header>An error has occurred</Header>;
  if (!data) return <div>Custom trip not found</div>;

  const trip = data;

  return (
    <div className="mt-3">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Custom Trip Details</h1>
        <div className="flex gap-2">
          <Link to="/admin/custom-trips">
            <Button variant="gray" extraClasses="flex items-center gap-2">
              <HiArrowLeft />
              Back to List
            </Button>
          </Link>
          <Button onClick={handleDelete} extraClasses="bg-red-500 hover:bg-red-600">
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card extra="p-6">
          <h2 className="text-xl font-semibold mb-4">Customer Information</h2>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-600">Name</label>
              <p className="font-medium">{trip.name}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Email</label>
              <p className="font-medium">{trip.email}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Phone</label>
              <p className="font-medium">{trip.phone || 'Not provided'}</p>
            </div>
            {trip.userId && (
              <div>
                <label className="text-sm text-gray-600">User Account</label>
                <p className="font-medium">Registered User</p>
              </div>
            )}
          </div>
        </Card>

        <Card extra="p-6">
          <h2 className="text-xl font-semibold mb-4">Trip Details</h2>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-600">Number of Days</label>
              <p className="font-medium">{trip.days}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Start Date</label>
              <p className="font-medium">{new Date(trip.startDate).toLocaleDateString()}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Destinations</label>
              <p className="font-medium">{trip.destinations?.join(', ')}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Room Sharing</label>
              <p className="font-medium">{trip.roomSharing}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Adults</label>
              <p className="font-medium">{trip.adults || 1}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Children</label>
              <p className="font-medium">{trip.children || 0}</p>
            </div>
          </div>
        </Card>

        <Card extra="p-6">
          <h2 className="text-xl font-semibold mb-4">Additional Information</h2>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-600">Budget</label>
              <p className="font-medium">{trip.budget || 'Not specified'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Special Requests</label>
              <p className="font-medium">{trip.specialRequests || 'None'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Request Date</label>
              <p className="font-medium">{new Date(trip.createdAt).toLocaleString()}</p>
            </div>
          </div>
        </Card>

        <Card extra="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Status & Notes</h2>
            <Button
              onClick={() => setIsEditing(!isEditing)}
              extraClasses="text-sm"
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </Button>
          </div>
          
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full mt-1 p-2 border rounded-lg"
                >
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="processing">Processing</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-600">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full mt-1 p-2 border rounded-lg"
                  rows={4}
                  placeholder="Add notes about this custom trip request..."
                />
              </div>
              <Button
                onClick={handleStatusUpdate}
                extraClasses="w-full"
              >
                {updateLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600">Current Status</label>
                <p className="font-medium capitalize">{status}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Admin Notes</label>
                <p className="font-medium">{adminNotes || 'No notes added'}</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

const CustomTripDetails = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <CustomTripDetailsContent />
    </QueryClientProvider>
  );
};

export default CustomTripDetails;